"use client";
import {
  ArrowRight as ArrowIcon,
  ChevronRight as ArrowRightIcon,
} from "lucide-react";
import useTranslate from "@app/_hooks/useTranslate";

import { ContentfulImage } from "@components/ContentfulImage";
import Link from "@components/Link";
import { Button } from "@components/ui";
import { Heading } from "@components/ui/Heading";
import { Paragraph } from "@components/ui/Paragraph";
import type { CampaignWithImageFragment } from "@server/gql/graphql";
import type { Maybe } from "graphql/jsutils/Maybe";
import { useMemo } from "react";

import type { MarketAwareSlug } from "@/utils/const";

export default function CampaignList({
  campaigns,
  details,
  termsLink,
}: {
  campaigns: Maybe<CampaignWithImageFragment>[];
  details: MarketAwareSlug;
  termsLink?: string | null;
}) {
  const t = useTranslate();
  const items = useMemo(
    () =>
      campaigns.filter((campaign) => {
        const today = new Date();
        const start = new Date(campaign?.campaignStart);
        const end = new Date(campaign?.campaignEnd);

        return (
          start.getTime() < today.getTime() && end.getTime() > today.getTime()
        );
      }),
    [campaigns],
  );
  return (
    <>
      <div className="text-muted flex justify-between px-3 text-base uppercase">
        <p className="hidden md:block">
          {(items.length ?? 0) > 0 && `${items.length} `}
          {t("campaignCountSuffix")}
        </p>
        {termsLink && (
          <Link
            locale={details.marketConfig.locale}
            href={termsLink}
            className="my-2 flex items-center gap-x-1 md:my-0"
          >
            {t("campaignTerms")}
            <ArrowRightIcon strokeWidth={1} className="size-4" />
          </Link>
        )}
      </div>
      <div className="my-4 mb-6 grid grid-cols-1 gap-x-4 gap-y-6 px-3 md:grid-cols-3 lg:mb-12 lg:grid-cols-4">
        {items.map((campaign) => (
          <Link
            key={campaign?.slug}
            locale={details.marketConfig.locale}
            href={campaign?.slug}
            className="group flex flex-col gap-y-2"
          >
            {campaign?.image && (
              <ContentfulImage
                className="aspect-portrait mb-2 w-full object-cover"
                desktopImage={campaign.image}
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1920px) 25vw, 30em"
              />
            )}
            <Heading size={5}>{campaign?.campaignTitle}</Heading>
            <Paragraph
              size={{
                initial: "small",
                lg: "medium",
              }}
            >
              {campaign?.shortDescription}
            </Paragraph>
            <Button
              className="mt-auto justify-start no-underline group-hover:underline"
              variant="link"
            >
              {t("campaignLinkText")}
              <ArrowIcon
                strokeWidth={1}
                className="size-4.5 transition-[opacity,transform] group-hover:translate-x-1 group-hover:opacity-100 supports-[hover]:opacity-0"
              />
            </Button>
          </Link>
        ))}
      </div>
    </>
  );
}
